import { put, takeLatest, call, select, delay, cancelled, take } from 'redux-saga/effects';
import { generateInvoicingReport } from 'api';
import {
  START_REPORT_POLLING,
  AUTH_REFRESH_SUCCESS,
  AUTH_REFRESH_FAILURE,
  AUTH_REFRESH_SKIP,
} from 'redux-api/action/actionTypes';
import { updateReportToast } from 'redux-api/action/reportToast';
import { refreshRequest } from 'redux-api/action/auth';
import selectAuth from 'redux-api/reselect/auth';

const selectConfig = (state) => state.config;

function* reportPollingSaga({ payload }) {
  const { languageCode } = yield select(selectConfig);
  const { startDate, endDate } = payload;

  let attempts = 0;
  const maxAttempts = 60; // 5 minuti (60 * 5 secondi)
  const pollInterval = 5000; // 5 secondi
  const maxStartedAttempts = 3; // Massimo 3 tentativi per lo stato "started"
  let startedAttempts = 0;

  try {
    // REFRESH TOKEN
    yield put(refreshRequest());
    const { type } = yield take([AUTH_REFRESH_SUCCESS, AUTH_REFRESH_FAILURE, AUTH_REFRESH_SKIP]);
    if (type === AUTH_REFRESH_FAILURE) throw new Error('401 - Unauthorized');

    const { signInUserSession, adminRequestedStreetlibInternalId } = yield select(selectAuth);
    const { accessToken } = signInUserSession;

    while (attempts < maxAttempts) {
      attempts += 1;

      try {
        const response = yield call(
          generateInvoicingReport,
          languageCode,
          accessToken,
          adminRequestedStreetlibInternalId,
          startDate,
          endDate,
        );

        if (response.data.status === 'ready') {
          // Report pronto
          yield put(
            updateReportToast({
              status: 'ready',
              downloadUrl: response.data.url,
              isDownloading: false,
            }),
          );

          // Download automatico in una nuova finestra
          setTimeout(() => window.open(response.data.url, '_blank'), 1000);
          return;
        }

        if (response.data.status === 'processing') {
          // Report in elaborazione, continua il polling
          startedAttempts = 0; // Reset del contatore started
          yield put(
            updateReportToast({
              status: 'processing',
            }),
          );
          yield delay(pollInterval);
        } else if (response.data.status === 'started') {
          // Report ancora in fase di avvio
          startedAttempts += 1;

          if (startedAttempts > maxStartedAttempts) {
            // Troppi tentativi in stato "started", va in errore
            yield put(
              updateReportToast({
                status: 'error',
                isDownloading: false,
              }),
            );
            return;
          }

          // Continua il polling per lo stato "started"
          yield delay(pollInterval);
        }

        // Altri stati non riconosciuti, continua il polling se non abbiamo superato il limite
        yield delay(pollInterval);
      } catch (error) {
        console.error('Error polling report status:', error);
        yield put(
          updateReportToast({
            status: 'error',
            isDownloading: false,
          }),
        );
        return;
      }
    }

    // Timeout
    yield put(
      updateReportToast({
        status: 'timeout',
        isDownloading: false,
      }),
    );
  } catch (error) {
    console.error('Error in report polling saga:', error);
    yield put(
      updateReportToast({
        status: 'error',
        isDownloading: false,
      }),
    );
  } finally {
    if (yield cancelled()) {
      // Cleanup se il saga viene cancellato
      console.log('Report polling saga cancelled');
    }
  }
}

export default function* watchReportToast() {
  yield takeLatest(START_REPORT_POLLING, reportPollingSaga);
}
